import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Banknote, Loader2, Plus, Trash2, Wallet, BellRing } from 'lucide-react';
import {
    Invoice,
    InvoiceConfig,
    InvoicePayment,
    PaymentDirection,
    PaymentMethod,
} from '../../types';
import { Button } from '../Button';
import {
    addPayment,
    addPaymentWithRemainder,
    createOverpaymentCredit,
    deletePayment,
    fetchPayments,
    invoiceOverpaymentCents,
    recordDunning,
    PaymentValidationError,
} from '../../services/invoicePayments';
import {
    dueDunningLevel,
    openAmountCents,
    resolveDunningLevels,
    validatePayment,
    ValidationMessage,
} from '../../services/invoiceLogic';
import { centsFromEuroInput, formatCents, formatCentsPlain } from '../../services/money';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { METHOD_LABEL } from './paymentBooking';
import { RemainderOption } from './RemainderOption';
import {
    checkRemainder,
    isRemainderMethod,
    paymentRemainder,
    RemainderKind,
} from '../../services/paymentRemainder';

/**
 * Zahlungen, Ueberzahlung und Mahnstufe einer ausgestellten Rechnung.
 *
 * Der Zahlungsstatus wird hier NICHT gesetzt. Er entsteht im Datenbank-Trigger
 * aus der Summe der Zahlungen; die Oberflaeche laedt den Beleg danach neu. Das
 * ist der Grund, warum jede Aktion mit onChanged endet statt mit einem lokalen
 * setState — der Browser kennt den neuen Status nicht, die Datenbank schon.
 */

const de = (iso?: string): string => (iso ? iso.split('-').reverse().join('.') : '—');

interface Props {
    invoice: Invoice;
    invoiceConfig: InvoiceConfig;
    today: string;
    /** Nach jeder Buchung: den Beleg frisch aus der Datenbank nachladen. */
    onChanged: () => void | Promise<void>;
    /**
     * Fehlt das Recht „Zahlungen buchen": der Hinweis dazu. Erfassen, Löschen
     * und Guthaben stehen dann gesperrt mit diesem Titel da; die Zahlungen
     * selbst bleiben lesbar.
     */
    buchenGesperrt?: string;
    /** Ebenso für die Mahnstufe (Recht „Rechnungen ausstellen“, Nr. 151). */
    mahnenGesperrt?: string;
}

export const PaymentsPanel: React.FC<Props> = ({
    invoice,
    invoiceConfig,
    today,
    onChanged,
    buchenGesperrt,
    mahnenGesperrt,
}) => {
    const gesperrt = buchenGesperrt !== undefined;
    const [payments, setPayments] = useState<InvoicePayment[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);

    const [direction, setDirection] = useState<PaymentDirection>('INCOMING');
    const [valueDate, setValueDate] = useState(today);
    const [amountInput, setAmountInput] = useState('');
    const [method, setMethod] = useState<PaymentMethod>('BANK_TRANSFER');
    const [reference, setReference] = useState('');
    const [note, setNote] = useState('');
    const [netInput, setNetInput] = useState('');
    const [taxInput, setTaxInput] = useState('');
    /** Was mit dem Rest passiert — nie vorbelegt, siehe RemainderOption. */
    const [remainderKind, setRemainderKind] = useState<RemainderKind | null>(null);

    /**
     * Zahlungen laden. Wirft weiter.
     *
     * Bewusst ohne eigenes catch: nach einer Buchung muss der Aufrufer
     * unterscheiden koennen zwischen "die Buchung ist gescheitert" und "die
     * Buchung lief, aber die Anzeige ist nicht mehr aktuell". Ein hier
     * geschlucktes Problem wuerde als Erfolg weiterlaufen.
     */
    const load = useCallback(async () => {
        setLoading(true);
        try {
            setPayments(await fetchPayments(invoice.id));
        } finally {
            setLoading(false);
        }
    }, [invoice.id]);

    useEffect(() => {
        void load().catch((e) =>
            setError(e instanceof Error ? e.message : 'Zahlungen konnten nicht geladen werden.'),
        );
    }, [load]);

    const open = openAmountCents(invoice);
    const over = invoiceOverpaymentCents(invoice);
    const amountCents = centsFromEuroInput(amountInput);
    const remainderCents =
        remainderKind && direction === 'INCOMING' && !isRemainderMethod(method) && amountCents != null
            ? paymentRemainder(invoice, amountCents)
            : 0;

    // Bei mehreren Steuersaetzen kann die Aufteilung nicht berechnet werden
    // (allocatePayment liefert dann bewusst null). Sie muss erfasst werden.
    const needsAllocation =
        invoice.totals.taxGroups.filter((g) => g.taxableNetCents !== 0).length > 1;

    const draft = useMemo(
        () => ({
            direction,
            valueDate,
            amountCents: amountCents ?? 0,
            method,
            reference: reference || undefined,
            note: note || undefined,
        }),
        [direction, valueDate, amountCents, method, reference, note],
    );

    const issues: ValidationMessage[] = useMemo(
        () =>
            amountInput.trim()
                ? [
                      ...validatePayment(draft, invoice, today),
                      // Skonto oder Einbehalt allein gebucht (als Zahlungsart):
                      // dieselben Pruefungen wie bei der Restwahl.
                      ...(isRemainderMethod(draft.method)
                          ? checkRemainder(invoice, draft.method, draft.amountCents, draft.valueDate)
                          : []),
                  ]
                : [],
        [draft, invoice, today, amountInput],
    );
    const blocking = issues.filter((m) => m.level === 'error');

    const dunning = useMemo(
        () => dueDunningLevel(invoice, today, resolveDunningLevels(invoiceConfig)),
        [invoice, today, invoiceConfig],
    );

    const resetForm = () => {
        setAmountInput('');
        setReference('');
        setNote('');
        setNetInput('');
        setTaxInput('');
        setDirection('INCOMING');
        setValueDate(today);
        setRemainderKind(null);
        setFormOpen(false);
    };

    const handleSave = async () => {
        if (amountCents == null) {
            setError('Der Betrag ist nicht lesbar. Beispiel: 1.190,00');
            return;
        }
        setBusy('save');
        setError(null);
        try {
            let allocation: { allocatedNetCents: number; allocatedTaxCents: number } | undefined;
            if (needsAllocation) {
                const net = centsFromEuroInput(netInput);
                const tax = centsFromEuroInput(taxInput);
                if (net == null || tax == null) {
                    throw new Error(
                        'Die Rechnung enthält mehrere Steuersätze. Bitte Netto und Umsatzsteuer der Zahlung angeben.',
                    );
                }
                allocation = { allocatedNetCents: net, allocatedTaxCents: tax };
            }
            const input = { ...draft, invoiceId: invoice.id, amountCents, allocation };
            // Mit Skonto oder Einbehalt gehen Zahlung und Rest in EINER
            // Anweisung in die Datenbank — beide oder keine.
            if (remainderKind && remainderCents > 0) {
                await addPaymentWithRemainder(
                    input,
                    { kind: remainderKind, amountCents: remainderCents },
                    invoice,
                    today,
                );
            } else {
                await addPayment(input, invoice, today);
            }
            // Ab hier ist das Geld gebucht. Scheitert das Nachladen, ist das
            // eine ANDERE Lage als eine gescheiterte Buchung — und die
            // Eingabemaske darf erst danach geleert werden, sonst tippt der
            // Nutzer die vermeintlich verlorene Zahlung ein zweites Mal ein.
            // Manuelle Zahlungen haben keine bank_tx_id und damit keinen
            // Doppelbuchungsschutz.
            try {
                await load();
                await onChanged();
            } catch (reloadErr) {
                setError(
                    `Die Zahlung wurde gebucht, die Ansicht konnte aber nicht aktualisiert werden (${
                        reloadErr instanceof Error ? reloadErr.message : 'unbekannter Fehler'
                    }). Bitte die Seite neu laden — NICHT erneut buchen.`,
                );
                return;
            }
            resetForm();
            setNotice(
                remainderKind && remainderCents > 0
                    ? `Zahlung und ${remainderKind === 'CASH_DISCOUNT' ? 'Skonto' : 'Sicherheitseinbehalt'} über ${formatCents(remainderCents)} gebucht.`
                    : 'Zahlung gebucht.',
            );
        } catch (e) {
            if (e instanceof PaymentValidationError) setError(e.message);
            else
                setError(
                    rechtFehlerOder(e, () =>
                        e instanceof Error ? e.message : 'Die Zahlung konnte nicht gebucht werden.',
                    ),
                );
        } finally {
            setBusy(null);
        }
    };

    const handleDelete = async (payment: InvoicePayment) => {
        // Rueckfrage mit Betrag und Datum: der Datenbank-Trigger rechnet
        // paid_cents danach neu, und die Rechnung faellt von "Bezahlt" auf
        // "Ueberfaellig" zurueck — ein Fehlklick ist von hier aus nicht sichtbar.
        const confirmed = window.confirm(
            `Zahlung über ${formatCents(payment.amountCents)} vom ${de(payment.valueDate)} wirklich löschen?\n\n` +
                'Der Zahlungsstand der Rechnung wird danach neu berechnet; sie kann dadurch wieder als offen oder überfällig gelten.',
        );
        if (!confirmed) return;
        const id = payment.id;
        setBusy(id);
        setError(null);
        try {
            await deletePayment(id);
            await load();
            await onChanged();
        } catch (e) {
            setError(
                rechtFehlerOder(e, () =>
                    e instanceof Error ? e.message : 'Die Zahlung konnte nicht gelöscht werden.',
                ),
            );
        } finally {
            setBusy(null);
        }
    };

    const handleCredit = async () => {
        // Bezugspunkt ist die Rechnung, nicht eine einzelne Zahlung: der
        // Ueberhang entsteht aus allen Buchungen zusammen. Die RPC schreibt nur
        // den noch nicht gutgeschriebenen Teil gut, ein zweiter Klick findet
        // nichts mehr vor.
        setBusy('credit');
        setError(null);
        try {
            await createOverpaymentCredit(
                invoice.id,
                `Überzahlung ${invoice.invoiceNumber ?? invoice.id}`,
            );
            await onChanged();
            setNotice(`Kundenguthaben über ${formatCents(over)} angelegt.`);
        } catch (e) {
            setError(
                rechtFehlerOder(e, () =>
                    e instanceof Error ? e.message : 'Das Guthaben konnte nicht angelegt werden.',
                ),
            );
        } finally {
            setBusy(null);
        }
    };

    const handleDunning = async () => {
        if (!dunning) return;
        setBusy('dunning');
        setError(null);
        try {
            await recordDunning(invoice.id, dunning.level);
            await onChanged();
            setNotice(`Mahnstufe ${dunning.level} vermerkt.`);
        } catch (e) {
            setError(
                rechtFehlerOder(e, () =>
                    e instanceof Error ? e.message : 'Die Mahnstufe konnte nicht vermerkt werden.',
                ),
            );
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                    <Banknote className="h-4 w-4 text-slate-400" />
                    Zahlungen ({payments.length})
                </h2>
                {!formOpen && (
                    <Button
                        variant="secondary"
                        onClick={() => setFormOpen(true)}
                        disabled={gesperrt}
                        title={buchenGesperrt}
                        className="!px-2 !py-1 !text-xs"
                    >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Zahlung erfassen
                    </Button>
                )}
            </div>

            {error && (
                <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    {error}
                </div>
            )}
            {notice && !error && (
                <div className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-700">
                    {notice}
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 py-3 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Zahlungen werden geladen …
                </div>
            ) : payments.length === 0 ? (
                <p className="py-2 text-xs text-slate-500">Noch keine Zahlung erfasst.</p>
            ) : (
                <table className="w-full text-xs">
                    <thead className="text-left text-slate-500">
                        <tr>
                            <th className="py-1">Wertstellung</th>
                            <th className="py-1">Art</th>
                            <th className="py-1">Verwendungszweck</th>
                            <th className="py-1 text-right">Betrag</th>
                            <th className="py-1" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {payments.map((p) => (
                            <tr key={p.id}>
                                <td className="py-1">{de(p.valueDate)}</td>
                                <td className="py-1">
                                    {METHOD_LABEL[p.method]}
                                    {p.direction === 'OUTGOING' && (
                                        <span className="ml-1 text-amber-700">(Erstattung)</span>
                                    )}
                                    {p.bankTxId && (
                                        <span className="ml-1 text-slate-400">· Bankimport</span>
                                    )}
                                </td>
                                <td className="py-1 text-slate-600">
                                    {p.reference || p.note || ''}
                                </td>
                                <td
                                    className={`py-1 text-right font-medium tabular-nums ${
                                        p.direction === 'OUTGOING' ? 'text-amber-700' : ''
                                    }`}
                                >
                                    {p.direction === 'OUTGOING' ? '- ' : ''}
                                    {formatCents(p.amountCents)}
                                </td>
                                <td className="py-1 text-right">
                                    <button
                                        type="button"
                                        onClick={() => void handleDelete(p)}
                                        disabled={busy === p.id || gesperrt}
                                        title={buchenGesperrt ?? 'Zahlung löschen'}
                                        className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                                    >
                                        {busy === p.id ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-3.5 w-3.5" />
                                        )}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-sm">
                <div className="flex justify-between">
                    <span className="text-slate-600">Zahlbetrag</span>
                    <span className="tabular-nums">{formatCents(invoice.totals.payableCents)}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-slate-600">bezahlt / verrechnet</span>
                    <span className="tabular-nums">
                        {formatCents(invoice.paidCents + invoice.creditedCents)}
                    </span>
                </div>
                <div className="flex justify-between font-semibold">
                    <span>offen</span>
                    <span className="tabular-nums">{formatCents(open)}</span>
                </div>
            </div>

            {over > 0 && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                    <span className="flex items-center gap-1.5">
                        <Wallet className="h-4 w-4" />
                        Überzahlung von {formatCents(over)}.
                    </span>
                    <Button
                        variant="secondary"
                        onClick={() => void handleCredit()}
                        disabled={busy === 'credit' || gesperrt}
                        title={buchenGesperrt}
                        className="!px-2 !py-1 !text-xs"
                    >
                        {busy === 'credit' ? 'Wird angelegt …' : 'Als Kundenguthaben führen'}
                    </Button>
                </div>
            )}

            {(invoice.dunningLevel > 0 || dunning) && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700">
                    <span className="flex items-center gap-1.5">
                        <BellRing className="h-4 w-4 text-slate-400" />
                        {invoice.dunningLevel > 0
                            ? `Mahnstufe ${invoice.dunningLevel}, zuletzt am ${de(
                                  invoice.dunningLastAt?.slice(0, 10),
                              )}.`
                            : 'Noch nicht gemahnt.'}
                        {dunning && ` Stufe ${dunning.level} ist fällig.`}
                    </span>
                    {dunning && (
                        <Button
                            variant="secondary"
                            onClick={() => void handleDunning()}
                            disabled={busy === 'dunning' || mahnenGesperrt !== undefined}
                            title={mahnenGesperrt}
                            className="!px-2 !py-1 !text-xs"
                        >
                            {busy === 'dunning'
                                ? 'Wird vermerkt …'
                                : `Mahnung Stufe ${dunning.level} vermerken`}
                        </Button>
                    )}
                </div>
            )}

            {formOpen && (
                <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Richtung</span>
                            <select
                                value={direction}
                                onChange={(e) => setDirection(e.target.value as PaymentDirection)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            >
                                <option value="INCOMING">Eingang</option>
                                <option value="OUTGOING">Erstattung</option>
                            </select>
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Wertstellung</span>
                            <input
                                type="date"
                                value={valueDate}
                                onChange={(e) => setValueDate(e.target.value)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Betrag €</span>
                            <input
                                inputMode="decimal"
                                value={amountInput}
                                onChange={(e) => setAmountInput(e.target.value)}
                                placeholder={formatCentsPlain(open)}
                                className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                            />
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Zahlungsart</span>
                            <select
                                value={method}
                                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            >
                                {(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => (
                                    <option key={m} value={m}>
                                        {METHOD_LABEL[m]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {direction === 'INCOMING' && !isRemainderMethod(method) && (
                        <RemainderOption
                            name={`panel-rest-${invoice.id}`}
                            invoice={invoice}
                            paymentCents={amountCents}
                            valueDate={valueDate}
                            value={remainderKind}
                            onChange={setRemainderKind}
                            disabled={busy === 'save'}
                        />
                    )}

                    {needsAllocation && (
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block text-xs">
                                <span className="mb-0.5 block text-slate-500">davon netto €</span>
                                <input
                                    inputMode="decimal"
                                    value={netInput}
                                    onChange={(e) => setNetInput(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                />
                            </label>
                            <label className="block text-xs">
                                <span className="mb-0.5 block text-slate-500">davon USt €</span>
                                <input
                                    inputMode="decimal"
                                    value={taxInput}
                                    onChange={(e) => setTaxInput(e.target.value)}
                                    className="w-full rounded border border-slate-300 px-2 py-1 text-right tabular-nums"
                                />
                            </label>
                            <p className="col-span-2 text-[11px] text-slate-500">
                                Die Rechnung enthält mehrere Steuersätze. Die Aufteilung wird nicht
                                geschätzt — sie muss angegeben werden, weil
                                Umsatzsteuervoranmeldung und DATEV-Export sie brauchen.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Verwendungszweck</span>
                            <input
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                        </label>
                        <label className="block text-xs">
                            <span className="mb-0.5 block text-slate-500">Notiz</span>
                            <input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                className="w-full rounded border border-slate-300 px-2 py-1"
                            />
                        </label>
                    </div>

                    {issues.length > 0 && (
                        <ul className="space-y-0.5 text-[11px]">
                            {issues.map((m) => (
                                <li
                                    key={m.code}
                                    className={
                                        m.level === 'error' ? 'text-red-600' : 'text-amber-700'
                                    }
                                >
                                    {m.message}
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button
                            variant="secondary"
                            onClick={resetForm}
                            className="!px-3 !py-1 !text-xs"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={() => void handleSave()}
                            disabled={busy === 'save' || blocking.length > 0 || amountCents == null}
                            className="!px-3 !py-1 !text-xs"
                        >
                            {busy === 'save' ? 'Wird gebucht …' : 'Zahlung buchen'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
